// src/pages/LoginPage.js
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO";
import GoogleButton from "../../components/common/GoogleButton";
import LinkedInButton from "../../components/common/LinkedInButton";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const API_BASE = process.env.REACT_APP_API_URL || "";
  const handleLogin = async (e) => {
    e.preventDefault(); // ✅ stop refresh
    try {
      const response = await fetch(`${API_BASE}/api/auth/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      // 🔎 Debug: log raw response text
      const rawText = await response.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }

      if (!response.ok) {
        toast({
          title: "Login failed",
          description: data.message || "Invalid credentials",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const accessToken = data.access_token;
      const refreshToken = data.refresh_token;

      if (!accessToken) {
        toast({
          title: "Login failed",
          description: "No access token returned.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // ✅ Save tokens
      localStorage.setItem("token", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      toast({
        title: "Login successful",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // If there's a returnTo URL, redirect there
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
        return;
      }

      // Otherwise, redirect based on landingFlag
      const flagRes = await fetch(`${API_BASE}/api/landing/get`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!flagRes.ok) {
        navigate("/dashboard");
        return;
      }

      const flagData = await flagRes.json();
      const landingFlag = flagData.landingFlag ?? 0;
      switch (landingFlag) {
        case 0:
          navigate("/me");
          break;
        case 1:
        case 2:
        case 3:
          navigate("/dashboard");
          break;
        case 4:
        case 5:
          navigate("/me/profile/resume-preview");
          break;
        default:
          navigate("/me");
          break;
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <SEO
        title="Sign In to SkillBack - Access Your Tech Resume Builder"
        description="Sign in to SkillBack and continue building your tech resume. Access your applications, resume builder, and career tools designed for developers and engineers."
        keywords="SkillBack login, sign in, tech resume access, developer login"
      />

      <Container display="flex" alignItems="flex-start" justifyContent="center">
      <Box
        as="form"
        onSubmit={handleLogin}
        mt="0"
        w={{ base: "90%", sm: "340px" }}
        p={{ base: "1.25rem", sm: "2rem" }}
        borderRadius="xl"
        boxShadow="md"
        bg={useColorModeValue("white", "gray.800")}
      >
        <Stack spacing="1.2rem" align="stretch">
          <Heading size="md" textAlign="center" mb="0.5rem">
            Sign In
          </Heading>

          <FormControl id="email">
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              placeholder="tanaki.haruki@skillback.io"
              size="sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormControl>

          <FormControl id="password">
            <FormLabel>Password</FormLabel>
            <InputGroup size="sm">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <InputRightElement>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  icon={showPassword ? <FaEye /> : <FaEyeSlash />}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Flex justify="flex-end">
            <Link
              as={NavLink}
              to="/ForgotPassword"
              fontSize="xs"
              color="blue.500"
              _hover={{ textDecoration: "underline" }}
            >
              Forgot password?
            </Link>
          </Flex>

          <Button
            type="submit"
            colorScheme="blue"
            size="sm"
            w="full"
            mt="0.5rem"
          >
            Log In
          </Button>

          <Text textAlign="center" fontSize="xs" color="gray.500">
            Don’t have an account?{" "}
            <Button
              as={NavLink}
              to="/signup"
              variant="link"
              colorScheme="blue"
              size="xs"
            >
              Sign Up
            </Button>
          </Text>

          <Flex align="center" my="0.5rem">
            <Divider />
            <Text px="3" fontSize="xs" color="gray.500" whiteSpace="nowrap">
              or sign in with
            </Text>
            <Divider />
          </Flex>

          <GoogleButton />
          <LinkedInButton />
        </Stack>
      </Box>
    </Container>
    </>
  );
}
